import React, { useEffect, useState } from "react";
import { authStore } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useNavigate } from "react-router-dom";
import { CalendarDays, ChevronLeft, ChevronRight, MapPin, FileDown, MessageSquare, Clock } from "lucide-react";
import { toast } from "./ui/sonner";
import { fmtD } from "../lib/date";

const MGR_ROLES = ["admin", "ceo", "sales_manager", "sales_vp", "eng_manager", "design_manager", "marketing", "marketing_manager"];
const rowKey = (i) => i.username || i.evaluator;
const slug = (s) => String(s).replace(/\W+/g, "_");

/* 单张预约卡: 已同意 / 旧字段 = 实色; 待回复 / 申请改期 = 浅色虚线 */
function ItemCard({ i, zh, onOpen }) {
  const pending = i.status === "pending" || i.status === "reschedule";
  return (
    <button data-testid={`eval-schedule-item-${i.leadId}${i.username ? `-${i.username}` : ""}`} data-status={i.status} onClick={onOpen}
      draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", JSON.stringify(i))}
      className={`block w-full text-left rounded-md border px-1.5 py-1 mb-1 cursor-grab active:cursor-grabbing ${pending ? "border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 opacity-80" : "border-amber-200 bg-amber-50 hover:bg-amber-100"}`}>
      <span className={`font-semibold ${pending ? "text-slate-500" : "text-amber-700"}`}>{i.time}</span> <span className="text-slate-700">{i.clientName}</span>
      {pending && <span className="ml-1 text-[9px] font-bold text-slate-400">{i.status === "reschedule" ? (zh ? "申请改期" : "reschedule") : (zh ? "待回复" : "pending")}</span>}
      {i.address && <span className="flex items-center gap-0.5 text-[10px] text-slate-400 truncate"><MapPin className="h-2.5 w-2.5 shrink-0" />{i.address}</span>}
    </button>
  );
}

export default function EvalScheduleBoard({ zh, region, regionLabel }) {
  const { api, user } = useApp();
  const nav = useNavigate();
  const [week, setWeek] = useState(0);
  const [tick, setTick] = useState(0);
  const [data, setData] = useState(null);
  const canView = MGR_ROLES.includes(user?.role);
  useEffect(() => {
    let current = true;
    setData(null);
    if (!canView) return () => { current = false; };
    const d = new Date(); d.setDate(d.getDate() + week * 7);
    const query = new URLSearchParams({ start: d.toISOString().slice(0, 10), ...(region ? { region } : {}) });
    api.get(`/crm/site-eval-schedule?${query.toString()}`).then((r) => { if (current) setData(r.data); }).catch(() => { if (current) setData(null); });
    return () => { current = false; };
  }, [week, canView, tick, region]); // eslint-disable-line
  const onDrop = async (e, row, date) => {
    e.preventDefault();
    let it; try { it = JSON.parse(e.dataTransfer.getData("text/plain")); } catch { return; }
    if (!it?.leadId || (it.date === date && rowKey(it) === rowKey(row))) return;
    const personChanged = rowKey(row) !== rowKey(it);
    const hasInvites = (it.invitees || []).length > 0;
    if (personChanged && hasInvites && !row.username) { toast.error(zh ? "有邀约名单的预约只能换到同事行" : "Invited appointments can only move to a colleague row"); return; }
    if (!window.confirm(zh
      ? `将 ${it.clientName || "客户"} 的现场评估从 ${fmtD(it.date)} ${it.time} 改到 ${date}${personChanged ? `，评估人员改为 ${row.evaluator}` : ""}？\n保存后将自动短信通知客户与销售${hasInvites ? "，邀约名单保留并重置为待回复重新通知" : ""}。`
      : `Move ${it.clientName}'s evaluation to ${date}${personChanged ? ` (${row.evaluator})` : ""}? Client & sales will be notified by SMS.`)) return;
    const body = { date, time: it.time || "09:00" };
    if (hasInvites) body.invitees = personChanged ? (it.invitees || []).map((u) => (u === it.username ? row.username : u)) : it.invitees;
    else body.evaluator = personChanged ? (row.evaluator === "未指定" ? "" : row.evaluator) : (it.evaluator === "未指定" ? "" : it.evaluator);
    const post = (force) => api.post(`/crm/leads/${it.leadId}/site-eval-appointment`, { ...body, force });
    try { await post(false); toast.success(zh ? "已改期并通知" : "Moved"); setTick((t) => t + 1); }
    catch (err) {
      const det = err?.response?.data?.detailRaw ?? err?.response?.data?.detail;
      if (err?.response?.status === 409 && det?.conflict) {
        if (window.confirm(`${det.message}\n${zh ? "存在时段冲突，仍要改期吗？" : "Conflict — move anyway?"}`)) {
          try { await post(true); toast.success(zh ? "已改期并通知" : "Moved"); setTick((t) => t + 1); } catch { toast.error(zh ? "改期失败" : "Failed"); }
        }
      } else toast.error(typeof det === "string" ? det : (zh ? "改期失败" : "Failed"));
    }
  };
  const sendWeekSms = async () => {
    if (!window.confirm(zh ? "立即向本周所有评估人员（已同意的同事）推送其本周评估安排？\n有电话的发短信+铃铛，无电话的仅铃铛提醒。" : "Push this week's schedule to all evaluators now (SMS + bell)?")) return;
    try {
      const { data: d } = await api.post("/crm/eval-week-sms-run", { force: true });
      const n = (d.results || []).length;
      const sms = (d.results || []).filter((r) => r.sms).length;
      toast.success(zh ? `已推送 ${n} 位评估人员（其中短信 ${sms} 条）` : `Notified ${n} evaluators (${sms} SMS)`);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "发送失败" : "Failed")); }
  };
  if (!canView || !data) return null;
  const dayNames = zh ? ["周一", "周二", "周三", "周四", "周五", "周六", "周日"] : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const items = data.items || [];
  const rows = [];
  for (const i of items) if (!rows.some((r) => rowKey(r) === rowKey(i))) rows.push({ username: i.username, evaluator: i.evaluator });
  const todayS = new Date().toISOString().slice(0, 10);
  const highlight = (item) => Boolean(region && (item.region === region || item.branch === region || item.leadRegion === region));
  const acceptedTotal = items.filter((i) => i.status === "accepted" || i.status === "legacy").length;
  const pendingTotal = items.length - acceptedTotal;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="eval-schedule-board" data-rows={rows.length} data-accepted={acceptedTotal} data-pending={pendingTotal}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <CalendarDays className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-semibold text-slate-800">{zh ? "现场评估日程（同事周历）" : "Site Evaluation Schedule"}</span>
          {regionLabel && <span className="text-[11px] text-slate-500">{zh ? `仅显示：${regionLabel}` : `Showing: ${regionLabel}`}</span>}
          <span className="text-[11px] text-slate-400">{data.days?.[0]} ~ {data.days?.[6]}</span>
          <span className="text-[10px] text-slate-400" data-testid="eval-schedule-totals">{zh ? `本周 ${acceptedTotal} 场已确认` : `${acceptedTotal} confirmed`}{pendingTotal ? (zh ? ` · ${pendingTotal} 待回复` : ` · ${pendingTotal} pending`) : ""}</span>
          <span className="text-[10px] text-slate-300 hidden sm:inline">{zh ? "· 已同意实色 / 待回复虚线 / 已拒绝不显示 · 拖动卡片改期或换人（邀约名单保留）" : "· solid = accepted, dashed = pending · drag to reschedule"}</span>
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="eval-schedule-sms" onClick={sendWeekSms}
            className="h-7 px-2 rounded-md border border-slate-200 hover:bg-slate-50 text-[11px] text-slate-600 inline-flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" />{zh ? "发本周安排" : "SMS week"}</button>
          <button data-testid="eval-schedule-pdf" onClick={() => { const query = new URLSearchParams({ start: data.weekStart, ...(region ? { region } : {}), auth: authStore.get("ali_token") || "" }); window.open(`${api.defaults.baseURL}/crm/site-eval-schedule-pdf?${query.toString()}`, "_blank"); }}
            className="h-7 px-2 rounded-md border border-slate-200 hover:bg-slate-50 text-[11px] text-slate-600 inline-flex items-center gap-1"><FileDown className="h-3.5 w-3.5" />{zh ? "导出 PDF" : "PDF"}</button>
          <button data-testid="eval-schedule-prev" onClick={() => setWeek((w) => w - 1)} className="h-7 w-7 rounded-md border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-500"><ChevronLeft className="h-4 w-4" /></button>
          <button data-testid="eval-schedule-today" onClick={() => setWeek(0)} className="h-7 px-2 rounded-md border border-slate-200 hover:bg-slate-50 text-[11px] text-slate-600">{zh ? "本周" : "This week"}</button>
          <button data-testid="eval-schedule-next" onClick={() => setWeek((w) => w + 1)} className="h-7 w-7 rounded-md border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-500"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-slate-300 text-center py-4" data-testid="eval-schedule-empty">{zh ? "本周暂无现场评估预约" : "No site evaluations this week"}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[860px]">
            <thead>
              <tr className="text-slate-400">
                <th className="text-left font-medium px-2 py-1.5 w-32">{zh ? "同事 / 评估人员" : "Colleague"}</th>
                {(data.days || []).map((d, i) => (
                  <th key={d} className={`text-left font-medium px-2 py-1.5 ${d === todayS ? "text-amber-600" : ""}`}>{dayNames[i]}<span className="block text-[10px] font-normal">{d.slice(5)}</span></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const mine = items.filter((i) => rowKey(i) === rowKey(row));
                const acc = mine.filter((i) => i.status === "accepted" || i.status === "legacy").length;
                const pend = mine.length - acc;
                const rowHighlighted = mine.some(highlight);
                const k = slug(rowKey(row));
                return (
                  <tr key={k} className={`border-t border-slate-100 align-top ${rowHighlighted ? "bg-indigo-50/70" : ""}`} data-testid={`eval-schedule-row-${k}`} data-selected={rowHighlighted ? "true" : "false"} data-accepted={acc} data-pending={pend}>
                    <td className="px-2 py-2 font-semibold text-slate-700">{row.evaluator}
                      <span className="block text-[9px] font-normal text-slate-400 inline-flex items-center gap-0.5" data-testid={`eval-schedule-count-${k}`}><Clock className="h-2.5 w-2.5" />{zh ? `本周 ${acc} 场` : `${acc} this week`}{pend ? (zh ? ` · ${pend} 待回复` : ` · ${pend} pending`) : ""}</span>
                      {data.acks?.[row.evaluator] && (data.acks[row.evaluator].acked
                        ? <span data-testid={`eval-ack-yes-${k}`} title={data.acks[row.evaluator].ackedAt || ""} className="block text-[9px] font-bold text-emerald-600">✓ {zh ? "已确认周安排" : "Acked"}</span>
                        : <span data-testid={`eval-ack-no-${k}`} className="block text-[9px] font-bold text-amber-500">{zh ? "周安排未确认" : "No ack"}</span>)}
                    </td>
                    {(data.days || []).map((d) => (
                      <td key={d} className={`px-2 py-2 ${d === todayS ? "bg-amber-50/50" : ""}`}
                        onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, row, d)} data-testid={`eval-schedule-cell-${k}-${d}`}>
                        {mine.filter((i) => i.date === d).map((i) => <ItemCard key={`${i.leadId}-${i.username || ""}`} i={i} zh={zh} onOpen={() => nav(`/crm/leads/${i.leadId}`)} />)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
